import { io } from 'socket.io-client';

// Same origin unless a split-origin dev setup names the API (VITE_API_URL),
// exactly like api-client's API_BASE. The old fallback was a literal
// http://localhost:10000, which every production build without VITE_API_URL
// baked in — so no deployed browser ever connected, and every live update
// (issues-changed, formula-store-updated, notifications-changed) silently
// fell back to polling. A deployed page always has an origin; use it.
const API_URL = import.meta.env.VITE_API_URL
  || (typeof window !== 'undefined' && window.location?.origin) || 'http://localhost:10000';

export const socket = io(API_URL, {
  autoConnect: false,
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionAttempts: Infinity,
  // Function form (supported by socket.io-client v3+) re-reads localStorage
  // on EVERY (re)connect attempt, so a token renewed while the socket was
  // down is picked up automatically on the next handshake.
  auth: (cb) => cb({ token: localStorage.getItem('token') }),
});

// socket.io-client treats a server middleware rejection (our io.use auth
// check) as FATAL: it destroys the manager subscription and stops
// reconnecting — `reconnectionAttempts: Infinity` does not apply to
// handshake rejections, only transport drops. Without this handler, one
// expired-token handshake would permanently kill live updates for the
// tab. Instead: wait, then connect() again — the auth callback re-reads
// localStorage, so a token renewed in the meantime gets picked up.
const AUTH_RETRY_DELAY_MS = 15_000;
socket.on('connect_error', (err) => {
  if (err?.message !== 'unauthorized') return; // transport errors auto-retry
  setTimeout(() => {
    if (!socket.connected && localStorage.getItem('token')) socket.connect();
  }, AUTH_RETRY_DELAY_MS);
});

// Shared dedup cache for 'issues-changed' events. The server broadcasts
// every webhook to ALL connected sockets. A single browser can
// temporarily hold 2+ sockets registered simultaneously during the
// window between a stale disconnect (refresh / network switch / tab
// close without a clean close frame) and the server's ping-timeout
// cleanup — typically 10–20 seconds. During that window the server's
// io.emit lands on every registered socket, so the same logical event
// arrives at the browser twice (or more).
//
// Dedup key: event + issueKey + server-assigned timestamp. Two truly
// distinct events (e.g., two updates of the same issue seconds apart)
// will always differ on timestamp. Duplicates from stale-socket
// broadcasts share all three fields and get dropped.
const seenEvents = new Map();
const DEDUP_WINDOW_MS = 10_000;

export function isDuplicateIssuesChangedEvent(payload) {
  const { event, issueKey, timestamp } = payload || {};
  if (!event || !issueKey || !timestamp) return false;
  const key = `${event}|${issueKey}|${timestamp}`;
  if (seenEvents.has(key)) return true;
  seenEvents.set(key, Date.now());
  // Keep the map bounded — prune entries older than the dedup window.
  if (seenEvents.size > 500) {
    const cutoff = Date.now() - DEDUP_WINDOW_MS;
    for (const [k, ts] of seenEvents) {
      if (ts < cutoff) seenEvents.delete(k);
    }
  }
  return false;
}
